import React, { useContext, useState, useRef, useCallback } from 'react';
import { Avatar, makeStyles, DialogTitle, DialogContent, DialogActions, Dialog, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import AttachmentIcon from '@material-ui/icons/Attachment';
import SendIcon from '@material-ui/icons/Send';

import { logEvent, unsubscribeRealtimeUpdates } from '../../system/firebase';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { LayoutContext, AppContext } from '../../common/contexts';
import { getNameByUser } from '../../common/textHelper';
import { PLACEHOLDER_FIELD_TO } from '../../common/placeholderHelper';
import useRequiredFields from '../../common/hooks/useRequiredFields';

import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdTextField from '../../common/elemental/AfitdTextField';
import AfitdButton from '../../common/elemental/AfitdButton';
import AfitdAutocomplete from '../../common/elemental/AfitdAutocomplete';
import AfitdFileTypeIcon from '../../common/elemental/AfitdFileTypeIcon';
import AfitdConfirmDialog from '../../common/elemental/AfitdConfirmDialog';
import AfitdMessageDialog from '../../common/elemental/AfitdMessageDialog';

import { sendNewInternalMessage, uploadThreadAttachment } from '../../common/services/empDashboardServices';

import _get from 'lodash/get';
import { ANALYTICS_EVENT_INTERNAL_MESSAGE_THREAD_CREATED } from '../../common/constants';

const useStyles = makeStyles(theme => ({
    fieldContainerGroup: {
        display: 'flex',
        margin: '10px 0',
        alignItems: 'center',
    },
    fieldContainer: { flex: 1, padding: '0 5px' },
    // fieldLabel: { fontWeight: 'bold', margin: '10px 0' },
    fieldLabel: { padding: '5px 0', marginTop: 20, fontWeight: 'bold' },
    subjectLimit: { marginTop: 10, color: 'grey' },
    messageField: { [theme.breakpoints.up('md')]: { minWidth: 600 } },
    buttonsContainer: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', margin: 16 },
    titleContainer: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontWeight: 'bold' },
    attachmentContainer: { display: 'flex', alignItems: 'center', marginRight: 5 },
    closeIcon: { cursor: 'pointer' },
    cancelButton: { marginRight: 16 },
    attachmentIcon: { cursor: 'pointer', border: '2px solid #F0F2F6', padding: 5, borderRadius: 50, color: 'rgba(17, 30, 20, 0.4)', fontSize: 35 },
    attachmentIconContainer: { padding: 0, marginRight: 16 },

    innerContainer: { padding: 20, border: '1px solid #D5D7DD', borderRadius: 4, marginTop: 10, background: 'rgba(255,255,255,0.9)' },
    fileTitle: { fontWeight: 'bold' },
    fileSize: { color: 'rgba(17, 30, 20, 0.4)' },
    inline: { display: 'flex', alignItems: 'center' },
    fileTextContainer: { marginLeft: 20 },
    recipientRowContainer: { display: 'flex', alignItems: 'center' },
}));

const getDefns = params => {
    const {
        props,
        classes,
        isDirty,
        jobSeekerId,
        jobSeekerName,
        jobSeekerProfilePicUrl,
        handleChangeSendTo,
        handleChangeJobTitle,
        handleChangeMessage,
        handleCloseDialog,
        handleClickSendButton,
        cleanUpAndClose,
        handleChangeAttachment,
        subject,
        cancelModal,
        setCancelModal,
        limitModalOpen,
        setLimitModalOpen,
        pageErrorDialogOpen,
        setPageErrorDialogOpen,
        getFieldError,
        recipients,
        members,
    } = params;

    const { open, jobSeekerId: jobSeekerIdProp, jobId: jobIdProp } = props;

    const recipient = { uid: jobSeekerId, jobSeekerName, profilePicUrl: jobSeekerProfilePicUrl }; //jobSeeker?.data;

    const sendButtonDisabled = !isDirty && (jobSeekerIdProp === undefined || jobIdProp === undefined);

    const defns = {
        root: { open, maxWidth: 'md', disableBackdropClick: true, disableEscapeKeyDown: true },
        sendButton: { onClick: handleClickSendButton, label: 'Send', startIcon: <SendIcon />, color: 'secondary', disabled: sendButtonDisabled },
        cancelButton: { onClick: handleCloseDialog, label: 'Cancel' },
        fieldLabel: { variant: 'body1' },
        subjectLimit: { variant: 'body2', align: 'right' },
        attachmentText: { variant: 'body1' },
        messageField: { placeholder: 'Type your message...', multiline: true, rows: 3, rowsMax: 5, onChange: handleChangeMessage, error: getFieldError('message') },
        closeIcon: { onClick: handleCloseDialog },
        attachmentInput: { type: 'file', hidden: true, onChange: handleChangeAttachment, accept: '.jpg, .png' },
        attachmentIconContainer: { component: 'label' },
        sendTo: {
            closeIcon: false,
            renderOption: option => <RecipientRow option={option} />,
            multiple: true, // set to multiple so the Chip is displayed but it is actually a single-select
            options: members,
            value: recipients,
            getOptionLabel: option => getNameByUser(option),
            renderInput: params => <AfitdTextField {...params} placeholder={recipient ? null : PLACEHOLDER_FIELD_TO} color="secondary" />,
            onChange: handleChangeSendTo,
            error: getFieldError('recipients'),
        },
        subject: {
            value: subject,
            onChange: handleChangeJobTitle,
            error: getFieldError('subject'),
            placeholder: 'Type your subject...',
        },
        confirmCancelDialog: {
            title: 'Cancel',
            open: cancelModal,
            onConfirm: () => {
                setCancelModal(false);
                cleanUpAndClose();
            },
            onClose: () => setCancelModal(false),
        },
        limitDialog: {
            open: limitModalOpen,
            onClose: () => setLimitModalOpen(false),
            onConfirm: () => setLimitModalOpen(false),
            yesButtonLabel: 'Upgrade',
            yesButtonColor: 'secondary',
            message: 'You have reached your limit for candidate engagement for this Tier.  Please click below to Upgrade your subscription',
            title: 'Interaction Limit Exceeded',
        },
        fileTitle: { variant: 'body2' },
        fileSize: { variant: 'caption' },
        pageErrorDialog: {
            message: 'You must enter all required fields.',
            open: pageErrorDialogOpen,
            onClose: () => setPageErrorDialogOpen(false),
        },
    };

    setClassNameInDefns(defns, classes);
    return defns;
};

const RecipientRow = props => {
    const classes = useStyles();
    const { option } = props;
    const { profilePicUrl, firstName, lastName } = option;
    return (
        <div className={classes.recipientRowContainer}>
            <div>
                <Avatar src={profilePicUrl} />
            </div>
            &nbsp;&nbsp;
            <div>
                {firstName} {lastName}
            </div>
        </div>
    );
};

const SendMessageDialog = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles();
    const { setSnackbarErrorMessage, setSnackbarMessage } = useContext(LayoutContext);
    const { userId, employerId, currentEmployer } = useContext(AppContext);
    const unsubscribeOnSnapshotListRef = useRef([]);

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { onClose } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [isDirty, setIsDirty] = useState(false);
    const [message, setMessage] = useState('');
    const [jobSeekerNames, setJobSeekerNames] = useState([]);
    const [recipients, setRecipients] = useState([]);
    const [subject, setSubject] = useState('');
    const [cancelModal, setCancelModal] = useState(false);
    const [limitModalOpen, setLimitModalOpen] = useState(false);
    const [pageErrorDialogOpen, setPageErrorDialogOpen] = useState(false);
    const { /*invalidRequiredFields, setInvalidRequiredFields,*/ getFieldError, clearInvalidRequiredField, validateRequiredFields } = useRequiredFields();
    const [file, setFile] = useState(null);

    //---------------------------------------------------------------------
    // derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Engage check
    //---------------------------------------------------------------------
    const currenntEmployerMembers = _get(currentEmployer, 'members');
    const members = currenntEmployerMembers?.filter(member => member?.userId !== userId);
    const senderMember = currenntEmployerMembers?.find(member => member?.userId === userId);

    //---------------------------------------------------------------------
    // useCallback
    //---------------------------------------------------------------------
    const getPageIsValid = useCallback(() => {
        const requiredFields = { recipients, subject, message };

        return validateRequiredFields(requiredFields);
    }, [recipients, subject, message, validateRequiredFields]);

    const cleanUpAndClose = useCallback(() => {
        if (unsubscribeOnSnapshotListRef.current) {
            unsubscribeRealtimeUpdates(unsubscribeOnSnapshotListRef.current);
            unsubscribeOnSnapshotListRef.current = [];
        }
        if (onClose) onClose();
    }, [onClose]);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Event handlers
    //---------------------------------------------------------------------
    const handleChangeSendTo = (evt, arr) => {
        // console.log('arr', arr);
        setRecipients(arr);
        setIsDirty(true);
        // clear error highlight
        clearInvalidRequiredField('recipients');
    };

    const handleChangeJobTitle = evt => {
        const value = evt.target.value;
        if (value?.length <= 100) {
            setSubject(value);
            setIsDirty(true);
            // clear error highlight
            clearInvalidRequiredField('subject');
        }
    };

    const handleChangeMessage = evt => {
        const value = evt.target.value;
        if (value?.length <= 500) {
            setMessage(value);
            setIsDirty(true);
            // clear error highlight
            clearInvalidRequiredField('message');
        }
    };

    const handleCloseDialog = () => {
        if (isDirty) {
            setCancelModal(true);
        } else {
            cleanUpAndClose();
        }
    };

    const handleClickSendButton = () => {
        if (getPageIsValid()) {
            let payload = {
                internal: true,
                messageDto: {
                    content: message,
                    createDt: new Date(),
                    senderId: userId,
                    first: true,
                },
                //? Members should include Sender,
                recipients,
                senderMember,
                employerId,
                employer: currentEmployer,
                subject,
                setSnackbarMessage,
                setSnackbarErrorMessage,
                postSaveActions: () => {
                    cleanUpAndClose();
                    // assuming each save is a new thread
                    logEvent(ANALYTICS_EVENT_INTERNAL_MESSAGE_THREAD_CREATED);
                },
            };
            if (file) {
                uploadThreadAttachment({
                    file,
                    senderId: userId,
                    onAttachmentUploadComplete: (file, url) => {
                        const { size, type, name } = file;
                        payload.messageDto.attachment = {
                            url,
                            title: name,
                            type,
                            size,
                        };

                        sendNewInternalMessage(payload);
                    },
                });
            } else {
                sendNewInternalMessage(payload);
            }
        } else {
            setPageErrorDialogOpen(true);
        }
    };

    const handleChangeAttachment = evt => {
        setFile(evt.target.files[0]);
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        props,
        classes,
        isDirty,
        // jobSeekerId,
        // jobSeekerName,
        // jobSeekerProfilePicUrl,
        handleChangeSendTo,
        handleChangeJobTitle,
        handleChangeMessage,
        handleCloseDialog,
        handleClickSendButton,
        cleanUpAndClose,
        message,
        setMessage,
        handleChangeAttachment,
        file,
        jobSeekerNames,
        // jobSeekers,
        setJobSeekerNames,
        subject,
        setSubject,
        // activeJobs,
        cancelModal,
        setCancelModal,
        limitModalOpen,
        setLimitModalOpen,
        pageErrorDialogOpen,
        setPageErrorDialogOpen,
        getFieldError,
        recipients,
        members,
        setRecipients,
        // setIsOkToSave,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <>
            <Dialog {...defns.root}>
                <DialogTitle>
                    <div {...defns.titleContainer}>
                        New Internal Message
                        <CloseIcon {...defns.closeIcon} />
                    </div>
                </DialogTitle>
                <DialogContent>
                    <div {...defns.fieldContainer}>
                        <AfitdTypography {...defns.fieldLabel}>To: *</AfitdTypography>
                        <AfitdAutocomplete {...defns.sendTo} />
                    </div>
                    <div {...defns.fieldContainer}>
                        <AfitdTypography {...defns.fieldLabel}>Subject: *</AfitdTypography>
                        <AfitdTextField {...defns.subject} />
                        <AfitdTypography {...defns.subjectLimit}>{subject?.length}/100</AfitdTypography>
                    </div>
                    <div {...defns.fieldContainer}>
                        <AfitdTypography {...defns.fieldLabel}>Message: *</AfitdTypography>
                        <AfitdTextField {...defns.messageField} />
                        <AfitdTypography {...defns.subjectLimit}>{message?.length}/500</AfitdTypography>
                    </div>
                    <div {...defns.fieldContainer}>
                        {file?.name && (
                            <div {...defns.innerContainer}>
                                <div {...defns.inline}>
                                    <a href="https://www.google.com/" target="_blank" rel="noopener noreferrer">
                                        <AfitdFileTypeIcon label={file?.type.split('/')[1].toUpperCase()} />
                                    </a>
                                    <div {...defns.fileTextContainer}>
                                        <AfitdTypography {...defns.fileTitle}>{file?.name}</AfitdTypography>
                                        <AfitdTypography {...defns.fileSize}>
                                            {file?.type.split('/')[1].toUpperCase()} {file?.type.split('/')[0]} {(file?.size / 1000).toFixed(2)} kB
                                        </AfitdTypography>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </DialogContent>
                <DialogActions>
                    <div {...defns.buttonsContainer}>
                        <div {...defns.attachmentContainer}>
                            <IconButton {...defns.attachmentIconContainer}>
                                <AttachmentIcon {...defns.attachmentIcon} />
                                <input {...defns.attachmentInput} />
                            </IconButton>
                            <AfitdTypography {...defns.attachmentText}>Attachment</AfitdTypography>
                        </div>

                        <div>
                            <AfitdButton {...defns.cancelButton} />
                            <AfitdButton {...defns.sendButton} />
                        </div>
                    </div>
                </DialogActions>
            </Dialog>
            <AfitdConfirmDialog {...defns.limitDialog} />
            <AfitdConfirmDialog {...defns.confirmCancelDialog} />
            <AfitdMessageDialog {...defns.pageErrorDialog} />
        </>
    );
};

export default SendMessageDialog;
